export const MAX_MEMORY_DOWNLOAD_BYTES = 64 * 1024 * 1024;

export function downloadBlob(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  link.hidden = true;
  document.body.append(link);
  link.click();
  link.remove();

  window.setTimeout(() => {
    URL.revokeObjectURL(objectUrl);
  }, 60_000);
}

interface WritableFileHandle {
  createWritable(): Promise<WritableStream<Uint8Array<ArrayBuffer>>>;
}

interface FileSystemDownloadWindow extends Window {
  showSaveFilePicker?: (options: {
    suggestedName: string;
    types: Array<{
      accept: Record<string, string[]>;
      description: string;
    }>;
  }) => Promise<WritableFileHandle>;
}

export type PreparedDownloadTarget =
  { kind: "automatic" } | { handle: WritableFileHandle; kind: "save_picker" };

export type DownloadStorageMode = "memory" | "save_picker";

interface DownloadResponseOptions {
  expectedBytes: number;
  onStorageMode?: (mode: DownloadStorageMode) => void;
  target: PreparedDownloadTarget;
}

function isCancelledPicker(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

export function responseFilename(
  response: Response,
  fallbackFilename: string,
): string {
  const disposition = response.headers.get("content-disposition") ?? "";
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const quoted = disposition.match(/filename="([^"]+)"/i)?.[1];
  const plain = disposition.match(/filename=([^;]+)/i)?.[1];
  let candidate: string;

  try {
    candidate = encoded
      ? decodeURIComponent(encoded)
      : (quoted ?? plain ?? fallbackFilename);
  } catch {
    candidate = fallbackFilename;
  }

  const safe = candidate.trim().split(/[\\/]/).at(-1)?.trim();
  return safe || fallbackFilename;
}

export async function prepareDownloadTarget(
  suggestedName: string,
): Promise<PreparedDownloadTarget> {
  const picker = (window as FileSystemDownloadWindow).showSaveFilePicker;
  if (!picker) {
    return { kind: "automatic" };
  }

  try {
    // Call before any JWT or network await so the browser still sees the
    // original button activation.
    const handle = await picker({
      suggestedName,
      types: [
        {
          accept: { "application/zip": [".zip"] },
          description: "Board Preserve archive",
        },
      ],
    });
    return { handle, kind: "save_picker" };
  } catch (error) {
    if (isCancelledPicker(error)) {
      throw error;
    }
    // Trello's iframe can expose the method while blocking its use. A bounded
    // memory fallback remains available for small archives only.
    return { kind: "automatic" };
  }
}

function responseBytes(
  response: Response,
  expectedBytes: number,
): number | null {
  const contentLength = response.headers.get("content-length");
  let headerBytes: number | null = null;
  if (contentLength && /^\d+$/.test(contentLength)) {
    const parsed = Number(contentLength);
    if (Number.isSafeInteger(parsed) && parsed > 0) {
      headerBytes = parsed;
    }
  }
  const retainedBytes = expectedBytes > 0 ? expectedBytes : null;
  if (headerBytes !== null && retainedBytes !== null) {
    return Math.max(headerBytes, retainedBytes);
  }
  return headerBytes ?? retainedBytes;
}

export async function downloadResponse(
  response: Response,
  fallbackFilename: string,
  options: DownloadResponseOptions,
): Promise<{ filename: string; storageMode: DownloadStorageMode }> {
  if (!response.ok) {
    throw new Error("The backup archive could not be downloaded.");
  }

  const filename = responseFilename(response, fallbackFilename);
  if (options.target.kind === "save_picker") {
    if (!response.body) {
      throw new Error(
        "The browser did not provide a downloadable data stream.",
      );
    }
    options.onStorageMode?.("save_picker");
    const writable = await options.target.handle.createWritable();
    await response.body.pipeTo(writable);
    return { filename, storageMode: "save_picker" };
  }

  const bytes = responseBytes(response, options.expectedBytes);
  if (bytes === null || bytes > MAX_MEMORY_DOWNLOAD_BYTES) {
    await response.body?.cancel();
    throw new Error(
      bytes === null
        ? "The archive size could not be verified, so Board Preserve refused to buffer it in modal memory. This browser must allow a Save As picker for this download."
        : `This ${Math.ceil(bytes / (1024 * 1024))} MiB archive is too large for the modal’s safe memory fallback. This browser must allow a Save As picker for this download.`,
    );
  }

  options.onStorageMode?.("memory");
  downloadBlob(await response.blob(), filename);
  return { filename, storageMode: "memory" };
}
